import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { EmployeeLayout } from '../components/EmployeeLayout';
import { availabilityAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getDayName } from '../lib/utils';
import { Calendar, Loader2, Save } from 'lucide-react';

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function Availability() {
  const { user } = useAuth();
  // Use the employee portal layout for staff so the sidebar/architecture
  // stays consistent with /employee/timesheets, /employee/schedule, etc.
  // Owners and managers continue to see the standard admin Layout.
  const ShellLayout = user?.role === 'staff' ? EmployeeLayout : Layout;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(
    DAYS.map(day => ({
      day_of_week: day.value,
      is_available: true,
      start_time: '09:00',
      end_time: '17:00',
    }))
  );

  useEffect(() => {
    fetchAvailability();
  }, [user]);

  const fetchAvailability = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await availabilityAPI.get(user.id);
      if (response.data && response.data.length > 0) {
        // Merge with defaults
        const merged = DAYS.map(day => {
          const existing = response.data.find(a => a.day_of_week === day.value);
          return existing || {
            day_of_week: day.value,
            is_available: true,
            start_time: '09:00',
            end_time: '17:00',
          };
        });
        setAvailability(merged);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (dayIndex) => {
    setAvailability(prev => prev.map((item, i) => 
      i === dayIndex ? { ...item, is_available: !item.is_available } : item
    ));
  };

  const handleTimeChange = (dayIndex, field, value) => {
    setAvailability(prev => prev.map((item, i) => 
      i === dayIndex ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await availabilityAPI.set(availability);
      toast.success('Availability saved!');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ShellLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </ShellLayout>
    );
  }

  return (
    <ShellLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              My Availability
            </h1>
            <p className="text-slate-500 mt-1">
              Set your weekly availability for scheduling
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-availability-btn">
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>

        {/* Availability Grid */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar size={20} className="text-slate-600" />
              Weekly Schedule
            </CardTitle>
            <CardDescription>
              Toggle days on/off and set your preferred hours
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {availability.map((day, index) => (
              <div
                key={day.day_of_week}
                className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg border ${
                  day.is_available ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                }`}
                data-testid={`availability-day-${day.day_of_week}`}
              >
                <div className="flex items-center justify-between sm:w-32">
                  <span className="font-medium text-slate-900">
                    {DAYS[index].label}
                  </span>
                  <Switch
                    checked={day.is_available}
                    onCheckedChange={() => handleToggle(index)}
                    data-testid={`availability-toggle-${day.day_of_week}`}
                  />
                </div>

                {day.is_available && (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1">
                      <Label className="sr-only">Start Time</Label>
                      <Input
                        type="time"
                        value={day.start_time}
                        onChange={(e) => handleTimeChange(index, 'start_time', e.target.value)}
                        className="w-full"
                        data-testid={`availability-start-${day.day_of_week}`}
                      />
                    </div>
                    <span className="text-slate-400">to</span>
                    <div className="flex-1">
                      <Label className="sr-only">End Time</Label>
                      <Input
                        type="time"
                        value={day.end_time}
                        onChange={(e) => handleTimeChange(index, 'end_time', e.target.value)}
                        className="w-full"
                        data-testid={`availability-end-${day.day_of_week}`}
                      />
                    </div>
                  </div>
                )}

                {!day.is_available && (
                  <span className="text-sm text-slate-500 italic">Not available</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="border-slate-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Setting your availability helps managers schedule shifts more efficiently. 
              Make sure to update it if your schedule changes!
            </p>
          </CardContent>
        </Card>
      </div>
    </ShellLayout>
  );
}
